
import React, { useState, useEffect } from 'react';
import { SpinnerIcon, TrashIcon, PlusIcon, PencilIcon, BuildingOfficeIcon, ChevronDownIcon, ChevronRightIcon, XMarkIcon, ChevronUpIcon } from '../../../components/Icons';
import { CIEN_CUADRAS_BUTTON_BLUE } from '../../../constants';
import { 
  fetchInmobiliariasConfigApi, 
  setInmobiliariaApi, 
  deleteInmobiliariaApi 
} from '../../../services/configApiClient';

interface InmobiliariaDoc {
    id: string;
    data: any;
}

// --- CONSTANTS & HELPERS ---

const VALID_DAYS = ["LUNES", "MARTES", "MIERCOLES", "JUEVES", "VIERNES", "SABADO", "DOMINGO"];

const cloneDeep = (obj: any) => JSON.parse(JSON.stringify(obj));

const setDeep = (obj: any, path: string[], value: any) => {
    if (path.length === 0) return value;
    const [head, ...tail] = path;
    if (tail.length === 0) {
        obj[head] = value;
    } else {
        if (!obj[head]) obj[head] = {}; 
        obj[head] = setDeep(obj[head], tail, value);
    }
    return obj;
};

const deleteDeep = (obj: any, path: string[]) => {
    if (path.length === 0) return obj;
    const [head, ...tail] = path;
    if (tail.length === 0) {
        delete obj[head];
    } else {
        if (obj[head]) {
            deleteDeep(obj[head], tail);
        }
    }
    return obj;
};

// Check if a specific path is protected from deletion based on business rules
const isProtectedPath = (path: string[]): boolean => {
    const len = path.length;
    if (len < 2) return false; 

    const key = path[len - 1];
    const parentKey = path[len - 2];
    const grandParentKey = len > 2 ? path[len - 3] : null;

    if (["COMISION", "DOCUMENTACION", "VISITA"].includes(key)) return true;
    if (parentKey === "COMISION" && ["BROKER", "BROKER_PREMIUM", "COMERCIAL_INTERNO"].includes(key)) return true;
    if (grandParentKey === "VISITA" && key === "HORARIO") return true; 
    if (parentKey && VALID_DAYS.includes(parentKey) && ["INICIO", "FIN"].includes(key)) return true;

    return false;
};

// --- COMPONENTS ---

const FirestoreValue: React.FC<{ value: any }> = ({ value }) => {
    if (value === null) return <span className="text-gray-400 italic">null</span>;
    if (typeof value === 'boolean') return <span className="text-purple-600 font-bold text-xs">{String(value)}</span>;
    if (typeof value === 'number') return <span className="text-[#f59e0b] font-mono text-xs">{value}</span>; 
    return <span className="text-[#16a34a] font-mono text-xs break-all">"{String(value)}"</span>; 
};

// 2. Generic Value Editor
const EditValueModal: React.FC<{ isOpen: boolean; onClose: () => void; initialValue: any; path: string[]; onSave: (val: any) => void }> = ({ isOpen, onClose, initialValue, path, onSave }) => {
    const [val, setVal] = useState(initialValue);
    // FIX: Cast the result of typeof to any to bypass TypeScript's exhaustive check against the restricted union type.
    const [type, setType] = useState<'string' | 'number' | 'boolean' | 'null'>((typeof initialValue === 'object' && initialValue === null ? 'null' : typeof initialValue) as any);
    const [error, setError] = useState<string | null>(null);

    const fieldName = path[path.length - 1];
    const parentName = path.length > 1 ? path[path.length - 2] : "";
    
    // Logic specifically for Commissions
    const isCommissionField = parentName === "COMISION";
    const isTimeField = VALID_DAYS.includes(parentName) && (fieldName === "INICIO" || fieldName === "FIN");

    useEffect(() => {
        setVal(initialValue);
        // Force type number for commissions
        if (isCommissionField || isTimeField) {
            setType('number');
        } else {
            // FIX: Cast result to any to match the restricted state type.
            setType((initialValue === null ? 'null' : typeof initialValue) as any);
        }
        setError(null);
    }, [initialValue, isOpen, isCommissionField, isTimeField]);

    if (!isOpen) return null;

    const handleSave = () => {
        let finalVal = val;
        
        if (type === 'number') {
            finalVal = Number(val);
            if (isTimeField) {
                if (finalVal < 0 || finalVal > 24) {
                    setError("La hora debe estar entre 0 y 24.");
                    return;
                }
            }
        }
        if (type === 'boolean') finalVal = val === 'true';
        if (type === 'null') finalVal = null;
        
        onSave(finalVal);
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-30 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded shadow-2xl p-6 w-full max-w-sm border-t-4 border-sky-600">
                <h3 className="font-bold text-gray-800 mb-4 text-sm">Editar valor de: <span className="text-sky-700 font-mono">{fieldName}</span></h3>
                
                {error && <div className="bg-red-100 text-red-700 p-2 rounded text-xs mb-3">{error}</div>}

                <div className="mb-4">
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Tipo</label>
                    <select value={type} onChange={e => setType(e.target.value as any)} className="w-full border-gray-300 rounded p-2 text-sm bg-gray-50" disabled={isTimeField || isCommissionField}>
                        <option value="string">Texto</option>
                        <option value="number">Número</option>
                        <option value="boolean">Booleano</option>
                        <option value="null">Nulo</option>
                    </select>
                </div>
                {type !== 'null' && (
                    <div className="mb-6">
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Valor</label>
                        {type === 'boolean' ? (
                            <select value={String(val)} onChange={e => setVal(e.target.value)} className="w-full border-gray-300 rounded p-2 text-sm focus:ring-sky-500 focus:border-sky-500">
                                <option value="true">true</option>
                                <option value="false">false</option>
                            </select>
                        ) : (
                            <div className="relative">
                                <input 
                                    type={type === 'number' ? 'number' : 'text'} 
                                    value={val || ''} 
                                    onChange={e => setVal(e.target.value)} 
                                    className="w-full border-gray-300 rounded p-2 text-sm focus:ring-sky-500 focus:border-sky-500 font-mono"
                                    step={isCommissionField ? "0.001" : (isTimeField ? "1" : "any")}
                                />
                                {isCommissionField && !isNaN(Number(val)) && (
                                    <div className="absolute right-2 top-2 text-xs font-bold text-green-600">
                                        {(Number(val) * 100).toFixed(2)}%
                                    </div>
                                )}
                            </div>
                        )}
                        {isTimeField && <p className="text-xs text-gray-400 mt-1">Formato hora militar (0-24)</p>}
                    </div>
                )}
                <div className="flex justify-end gap-2">
                    <button onClick={onClose} className="px-4 py-2 text-gray-600 text-sm hover:bg-gray-100 rounded">Cancelar</button>
                    <button onClick={handleSave} className="px-4 py-2 text-white text-sm rounded hover:opacity-90 font-medium" style={{ backgroundColor: CIEN_CUADRAS_BUTTON_BLUE }}>Actualizar</button>
                </div>
            </div>
        </div>
    );
};

// 3. Smart Add Field Modal (Context Aware)
const AddFieldModal: React.FC<{ isOpen: boolean; onClose: () => void; parentPath: string[]; onSave: (key: string, val: any) => void }> = ({ isOpen, onClose, parentPath, onSave }) => {
    const [key, setKey] = useState('');
    const [type, setType] = useState('string');
    const [value, setValue] = useState<any>('');
    const [daySchedule, setDaySchedule] = useState({ start: 8, end: 17 });

    const parentKey = parentPath[parentPath.length - 1];
    const isHorarioContext = parentKey === "HORARIO";

    useEffect(() => {
        setKey(''); 
        setType(isHorarioContext ? 'schedule_day' : 'string'); 
        setValue('');
    }, [isOpen, isHorarioContext]);

    if (!isOpen) return null;

    const handleSave = () => {
        if (!key.trim()) return alert("La clave es requerida");
        
        let finalVal = value;

        if (type === 'schedule_day') {
            if (daySchedule.start >= daySchedule.end) return alert("La hora de inicio debe ser menor a la de fin");
            finalVal = { INICIO: Number(daySchedule.start), FIN: Number(daySchedule.end) };
        } else {
            if (type === 'number') finalVal = Number(value);
            if (type === 'boolean') finalVal = value === 'true';
            if (type === 'null') finalVal = null;
            if (type === 'object') finalVal = {}; 
        }
        
        onSave(key.trim().toUpperCase(), finalVal);
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-30 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded shadow-xl p-6 w-full max-sm:max-w-xs">
                <h3 className="font-bold text-gray-800 mb-2">Agregar en: <span className="text-sky-600 font-mono text-sm">{parentKey}</span></h3>
                
                <div className="space-y-4 mt-4">
                    {isHorarioContext ? (
                        <div className="bg-orange-50 p-3 rounded border border-orange-100">
                            <label className="block text-xs font-bold text-orange-800 uppercase mb-2">Día de la Semana</label>
                            <select 
                                value={key} 
                                onChange={e => setKey(e.target.value)} 
                                className="w-full border-orange-300 rounded p-2 text-sm focus:ring-orange-500 mb-3"
                            >
                                <option value="">Selecciona un día...</option>
                                {VALID_DAYS.map(d => <option key={d} value={d}>{d}</option>)}
                            </select>
                            
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <label className="block text-[10px] text-gray-500 uppercase">Inicio (0-23)</label>
                                    <input type="number" min="0" max="23" value={daySchedule.start} onChange={e => setDaySchedule(p => ({...p, start: Number(e.target.value)}))} className="w-full border-gray-300 rounded text-sm"/>
                                </div>
                                <div>
                                    <label className="block text-[10px] text-gray-500 uppercase">Fin (1-24)</label>
                                    <input type="number" min="1" max="24" value={daySchedule.end} onChange={e => setDaySchedule(p => ({...p, end: Number(e.target.value)}))} className="w-full border-gray-300 rounded text-sm"/>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Nombre del Campo (Clave)</label>
                                <input type="text" value={key} onChange={e => setKey(e.target.value.toUpperCase())} className="w-full border-gray-300 rounded p-2 text-sm font-mono uppercase" placeholder="Ej: NUEVO_CAMPO" />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Tipo de Valor</label>
                                <select value={type} onChange={e => setType(e.target.value)} className="w-full border-gray-300 rounded p-2 text-sm">
                                    <option value="string">Texto</option>
                                    <option value="number">Número</option>
                                    <option value="boolean">Booleano</option>
                                    <option value="object">Objeto (Carpeta)</option>
                                    <option value="null">Nulo</option>
                                </select>
                            </div>
                            {type !== 'object' && type !== 'null' && (
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Valor Inicial</label>
                                    {type === 'boolean' ? (
                                        <select value={value} onChange={e => setValue(e.target.value)} className="w-full border-gray-300 rounded p-2 text-sm"><option value="true">True</option><option value="false">False</option></select>
                                    ) : (
                                        <input type={type === 'number' ? 'number' : 'text'} value={value} onChange={e => setValue(e.target.value)} className="w-full border-gray-300 rounded p-2 text-sm" />
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </div>
                <div className="flex justify-end gap-2 mt-6">
                    <button onClick={onClose} className="px-4 py-2 text-gray-600 text-sm hover:bg-gray-100 rounded">Cancelar</button>
                    <button onClick={handleSave} className="px-4 py-2 text-white text-sm rounded hover:opacity-90 font-medium" style={{ backgroundColor: CIEN_CUADRAS_BUTTON_BLUE }}>Agregar</button>
                </div>
            </div>
        </div>
    );
};

// --- TREE ITEM COMPONENT ---
interface TreeItemProps {
    name: string;
    value: any;
    path: string[];
    depth: number;
    onEditValue: (path: string[], val: any) => void;
    onAddChild: (path: string[]) => void;
    onDelete: (path: string[]) => void;
}

const FirestoreTreeItem: React.FC<TreeItemProps> = ({ name, value, path, depth, onEditValue, onAddChild, onDelete }) => {
    const [expanded, setExpanded] = useState(depth < 2);
    const isObject = value !== null && typeof value === 'object' && !Array.isArray(value);
    const protectedNode = isProtectedPath(path);
    const parentKey = path.length > 1 ? path[path.length - 2] : "";
    const isInsideHorario = parentKey === "HORARIO"; 

    return (
        <div className="font-mono text-sm ml-6 border-l border-gray-200 pl-2 relative">
            <div className="absolute top-3 -left-6 w-4 h-px bg-gray-300"></div>

            <div className="flex items-center group py-1 pr-2 hover:bg-sky-50 rounded transition-colors -ml-2 pl-2">
                {isObject ? (
                    <button onClick={() => setExpanded(!expanded)} className="mr-1 text-gray-500 focus:outline-none hover:text-gray-800">
                        {expanded ? <ChevronDownIcon className="w-3 h-3" /> : <ChevronRightIcon className="w-3 h-3" />}
                    </button>
                ) : <div className="w-3 h-3 mr-1"></div>}
                
                <span className={`mr-2 font-medium ${isObject ? 'text-sky-900' : 'text-purple-900'}`}>{name}:</span>
                
                {!isObject ? (
                    <FirestoreValue value={value} />
                ) : (
                    <span className="text-gray-400 text-xs italic">{expanded ? '' : `{ ${Object.keys(value).length} campos }`}</span>
                )}

                <div className="ml-auto opacity-0 group-hover:opacity-100 flex gap-1 transition-opacity">
                    {isObject && (
                        <button 
                            onClick={() => onAddChild(path)}
                            className="text-gray-400 hover:text-green-600 p-1 rounded-full hover:bg-green-50" 
                            title="Agregar Campo"
                        >
                            <PlusIcon className="w-3.5 h-3.5" />
                        </button>
                    )}

                    {!isObject && (
                        <button onClick={() => onEditValue(path, value)} className="text-gray-400 hover:text-sky-600 p-1 rounded-full hover:bg-sky-50" title="Editar Valor">
                            <PencilIcon className="w-3.5 h-3.5" />
                        </button>
                    )}
                    
                    {!protectedNode && (
                        <button onClick={() => onDelete(path)} className="text-gray-400 hover:text-red-600 p-1 rounded-full hover:bg-red-50" title="Eliminar Campo">
                            <TrashIcon className="w-3.5 h-3.5" />
                        </button>
                    )}
                </div>
            </div>
            
            {isObject && expanded && (
                <div className="ml-1">
                    {Object.entries(value).sort(([a], [b]) => {
                        if (name === "HORARIO") {
                            const indexA = VALID_DAYS.indexOf(a);
                            const indexB = VALID_DAYS.indexOf(b);
                            if (indexA !== -1 && indexB !== -1) return indexA - indexB;
                        }
                        return a.localeCompare(b);
                    }).map(([key, val]) => (
                        <FirestoreTreeItem 
                            key={key} 
                            name={key} 
                            value={val} 
                            path={[...path, key]}
                            depth={depth + 1} 
                            onEditValue={onEditValue}
                            onAddChild={onAddChild}
                            onDelete={onDelete}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

// --- CREATE MODAL (STRUCTURED) ---
const CreateInmobiliariaModal: React.FC<{ isOpen: boolean; onClose: () => void; onSave: (data: { id?: string, bankKey?: string, data: any }, isRoot: boolean) => void; parentDocId?: string | null }> = ({ isOpen, onClose, onSave, parentDocId }) => {
    const [docId, setDocId] = useState('');
    const [bankName, setBankName] = useState('');
    const [comBroker, setComBroker] = useState(0);
    const [comPremium, setComPremium] = useState(0);
    const [comInternal, setComInternal] = useState(0);
    const [anticipation, setAnticipation] = useState(0);

    useEffect(() => {
        if(isOpen) {
            setDocId('');
            setBankName('');
            setComBroker(0);
            setComPremium(0);
            setComInternal(0);
            setAnticipation(0);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const handleSave = () => {
        const isRoot = !parentDocId;
        
        if (isRoot && !docId.trim()) return alert("El ID del documento es requerido");
        if (!bankName.trim()) return alert("El Nombre Interno (Clave) es requerido");

        const normalizedKey = bankName.toUpperCase().replace(/\s+/g, '_');

        const structure = {
            [normalizedKey]: {
                COMISION: {
                    BROKER: Number(comBroker),
                    BROKER_PREMIUM: Number(comPremium),
                    COMERCIAL_INTERNO: Number(comInternal)
                },
                DOCUMENTACION: {
                    APLICA: null,
                    CONTACTO: null,
                    NO_APLICA: null
                },
                GESTION_VISITA: "",
                INVENTARIO: "Cargue directo de la inmobiliaria",
                VISITA: {
                    BROKER: {
                        DIAS_ANTICIPACION: Number(anticipation),
                        HORARIO: {}
                    },
                    COMERCIAL: {
                        DIAS_ANTICIPACION: Number(anticipation),
                        HORARIO: {}
                    }
                }
            }
        };

        if (isRoot) {
            // For a new root doc, we pass the structure as the root data
            // Usually if it's NOT the aggregated doc (183150), people expect the structure directly?
            // However, based on the previous JSON example, even single docs seem to wrap in a key (e.g. CASA-360).
            onSave({ id: docId.trim(), data: structure }, true);
        } else {
            // Adding to 183150, just pass the new key object
            onSave({ bankKey: normalizedKey, data: structure[normalizedKey] }, false);
        }
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-lg flex flex-col max-h-[90vh]">
                <div className="px-6 py-4 border-b flex justify-between items-center">
                    <h3 className="text-lg font-bold text-gray-900">
                        {parentDocId ? 'Agregar Banco a 183150' : 'Crear Nueva Inmobiliaria'}
                    </h3>
                    <button onClick={onClose}><XMarkIcon className="w-5 h-5 text-gray-400"/></button>
                </div>
                
                <div className="p-6 overflow-y-auto space-y-5">
                    {!parentDocId && (
                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">ID Documento (Firestore ID)</label>
                            <input type="text" value={docId} onChange={e => setDocId(e.target.value)} className="w-full border rounded p-2 text-sm" placeholder="Ej: 123456" />
                        </div>
                    )}
                    
                    <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Nombre Interno (Clave)</label>
                        <input type="text" value={bankName} onChange={e => setBankName(e.target.value.toUpperCase())} className="w-full border rounded p-2 text-sm uppercase font-mono" placeholder="Ej: MI_BANCO" />
                        <p className="text-xs text-gray-400 mt-1">Se convertirá a mayúsculas y guiones bajos.</p>
                    </div>

                    <div className="bg-gray-50 p-3 rounded border border-gray-200">
                        <h4 className="text-xs font-bold text-gray-700 mb-2 border-b pb-1">Comisiones (Decimal, ej: 0.03 = 3%)</h4>
                        <div className="grid grid-cols-3 gap-3">
                            <div>
                                <label className="block text-[10px] text-gray-500 mb-1">Broker</label>
                                <input type="number" step="0.001" value={comBroker} onChange={e => setComBroker(parseFloat(e.target.value))} className="w-full border rounded p-1.5 text-sm" />
                            </div>
                            <div>
                                <label className="block text-[10px] text-gray-500 mb-1">Premium</label>
                                <input type="number" step="0.001" value={comPremium} onChange={e => setComPremium(parseFloat(e.target.value))} className="w-full border rounded p-1.5 text-sm" />
                            </div>
                            <div>
                                <label className="block text-[10px] text-gray-500 mb-1">Interno</label>
                                <input type="number" step="0.001" value={comInternal} onChange={e => setComInternal(parseFloat(e.target.value))} className="w-full border rounded p-1.5 text-sm" />
                            </div>
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Días de Anticipación (Visitas)</label>
                        <input type="number" min="0" value={anticipation} onChange={e => setAnticipation(parseInt(e.target.value))} className="w-24 border rounded p-2 text-sm" />
                    </div>
                </div>

                <div className="flex justify-end gap-2 px-6 py-4 border-t bg-gray-50 rounded-b-lg">
                    <button onClick={onClose} className="px-3 py-1.5 text-gray-600 border rounded hover:bg-gray-50 text-sm">Cancelar</button>
                    <button onClick={handleSave} className="px-3 py-1.5 text-white rounded hover:opacity-90 text-sm" style={{ backgroundColor: CIEN_CUADRAS_BUTTON_BLUE }}>
                        {parentDocId ? 'Agregar Banco' : 'Crear Inmobiliaria'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// --- MAIN COMPONENT ---

import { AdminInmobiliariaCsvModal } from './AdminInmobiliariaCsvModal';

export const AdminInmobiliariasManagement: React.FC = () => {
    const [docs, setDocs] = useState<InmobiliariaDoc[]>([]);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState<{type: 'success'|'error', text: string} | null>(null);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [createTargetDocId, setCreateTargetDocId] = useState<string | null>(null);
    const [collapsedDocs, setCollapsedDocs] = useState<Set<string>>(new Set());
    const [isCsvModalOpen, setIsCsvModalOpen] = useState(false);
    const [csvTargetDocId, setCsvTargetDocId] = useState<string | null>(null);

    // Editing State
    const [editValueModal, setEditValueModal] = useState<{ open: boolean, path: string[], value: any } | null>(null);
    const [addFieldModal, setAddFieldModal] = useState<{ open: boolean, parentPath: string[] } | null>(null);

    const fetchDocs = async () => {
        setLoading(true);
        try {
            const dataObj = await fetchInmobiliariasConfigApi(true);
            const fetched: InmobiliariaDoc[] = Object.keys(dataObj).map(id => ({ id, data: dataObj[id] }));
            fetched.sort((a, b) => {
                if (a.id === '183150') return -1;
                if (b.id === '183150') return 1;
                return a.id.localeCompare(b.id);
            });
            setDocs(fetched);
        } catch (e) {
            console.error(e);
            setMessage({ type: 'error', text: 'Error al cargar inmobiliarias.' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchDocs(); }, []);

    const toggleCollapse = (docId: string) => {
        setCollapsedDocs(prev => {
            const newSet = new Set(prev);
            if (newSet.has(docId)) newSet.delete(docId);
            else newSet.add(docId);
            return newSet;
        });
    };

    const applyUpdate = async (docId: string, newData: any) => {
        try {
            await setInmobiliariaApi(docId, newData);
            setDocs(prev => prev.map(d => d.id === docId ? { ...d, data: newData } : d));
            setMessage({ type: 'success', text: 'Cambios guardados.' });
            setTimeout(() => setMessage(null), 2500);
        } catch (e: any) {
            setMessage({ type: 'error', text: `Error: ${e.message}` });
        }
    };

    const handleEditValue = (path: string[], currentValue: any) => {
        setEditValueModal({ open: true, path, value: currentValue });
    };

    const handleSaveValue = (newValue: any) => {
        if (!editValueModal) return;
        const [docId, ...fieldPath] = editValueModal.path;
        const docData = docs.find(d => d.id === docId)?.data;
        if (!docData) return;

        const newDocData = cloneDeep(docData);
        setDeep(newDocData, fieldPath, newValue);
        applyUpdate(docId, newDocData);
    };

    const handleAddChild = (path: string[]) => {
        setAddFieldModal({ open: true, parentPath: path });
    };

    const handleSaveNewField = (key: string, value: any) => {
        if (!addFieldModal) return;
        const [docId, ...fieldPath] = addFieldModal.parentPath;
        const docData = docs.find(d => d.id === docId)?.data;
        if (!docData) return;

        const newDocData = cloneDeep(docData);
        setDeep(newDocData, [...fieldPath, key], value);
        applyUpdate(docId, newDocData);
    };

    const handleDelete = (path: string[]) => {
        if (isProtectedPath(path)) {
            alert("Este campo está protegido y no se puede eliminar.");
            return;
        }
        if (!window.confirm("¿Estás seguro de eliminar este campo?")) return;
        const [docId, ...fieldPath] = path;
        const docData = docs.find(d => d.id === docId)?.data;
        if (!docData) return;

        const newDocData = cloneDeep(docData);
        deleteDeep(newDocData, fieldPath);
        applyUpdate(docId, newDocData);
    };

    // Handle saving from the structured Create Modal
    const handleCreateSave = async ({ id, bankKey, data }: { id?: string, bankKey?: string, data: any }, isRoot: boolean) => {
        try {
            if (isRoot && id) {
                // Creating a completely new document
                await setInmobiliariaApi(id, data);
                setMessage({ type: 'success', text: `Inmobiliaria ${id} creada.` });
            } else if (!isRoot && bankKey && createTargetDocId) {
                // Adding a key to an existing document (e.g., 183150)
                const docData = docs.find(d => d.id === createTargetDocId)?.data || {};
                const newDocData = { ...docData, [bankKey]: data };
                await setInmobiliariaApi(createTargetDocId, newDocData);
                setMessage({ type: 'success', text: `Banco ${bankKey} agregado a ${createTargetDocId}.` });
            }
            fetchDocs();
        } catch (e: any) {
            setMessage({ type: 'error', text: e.message });
        }
    };

    const handleDeleteDoc = async (id: string) => {
        if(!window.confirm(`¿Eliminar documento ${id} completo?`)) return;
        try {
            await deleteInmobiliariaApi(id);
            setMessage({type: 'success', text: 'Documento eliminado.'});
            fetchDocs();
        } catch (e: any) {
            setMessage({type:'error', text: e.message});
        }
    }

    const openCreateRootModal = () => {
        setCreateTargetDocId(null);
        setIsCreateModalOpen(true);
    };

    const openAddBankModal = (docId: string) => {
        setCreateTargetDocId(docId);
        setIsCreateModalOpen(true);
    };

    const openCsvModal = (docId: string) => {
        setCsvTargetDocId(docId);
        setIsCsvModalOpen(true);
    };

    const getDocDisplayName = (id: string, data: any) => {
        if (id === '183150') return 'Agrupador Principal';
        const keys = Object.keys(data);
        if (keys.length > 0) {
            const firstKeyData = data[keys[0]];
            return firstKeyData.NOMBRE || firstKeyData.RAZON_SOCIAL || keys[0];
        }
        return id;
    };

    return (
        <>
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center text-sky-600">
                    <BuildingOfficeIcon className="w-8 h-8 mr-3" />
                    <div>
                        <h2 className="text-xl font-bold">Gestión de Inmobiliarias</h2>
                        <p className="text-sm text-gray-500">Configuración estructurada de comisiones y reglas.</p>
                    </div>
                </div>
                <button 
                    onClick={openCreateRootModal}
                    className="flex items-center text-sm font-medium text-white px-4 py-2 rounded shadow-sm hover:opacity-90 transition-opacity"
                    style={{backgroundColor: CIEN_CUADRAS_BUTTON_BLUE}}
                >
                    <PlusIcon className="w-5 h-5 mr-2" /> Nueva Inmobiliaria
                </button>
            </div>

            {message && (
                <div className={`p-4 rounded-md mb-6 flex justify-between items-center animate-fade-in-down ${message.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
                    <span>{message.text}</span>
                    <button onClick={() => setMessage(null)} className="font-bold text-lg">&times;</button>
                </div>
            )}

            {loading ? (
                <div className="flex justify-center py-10"><SpinnerIcon className="w-10 h-10 text-sky-600"/></div>
            ) : (
                <div className="space-y-6">
                    {docs.map(docData => {
                        const isCollapsed = collapsedDocs.has(docData.id);
                        const is183150 = docData.id === '183150';

                        return (
                            <div key={docData.id} className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden transition-all duration-200">
                                <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex justify-between items-center select-none">
                                    <div className="flex items-center cursor-pointer" onClick={() => toggleCollapse(docData.id)}>
                                        <button className="mr-2 text-gray-500 focus:outline-none">
                                            {isCollapsed ? <ChevronDownIcon className="w-5 h-5" /> : <ChevronUpIcon className="w-5 h-5" />}
                                        </button>
                                        <h3 className="font-bold text-gray-800 font-mono text-lg">{docData.id}</h3>
                                        {is183150 && <span className="ml-3 bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded-full font-bold">Principal / Agrupador</span>}
                                    </div>
                                    <div className="flex space-x-2">
                                        <button 
                                            onClick={() => openCsvModal(docData.id)}
                                            className="text-green-600 hover:text-green-800 p-1 rounded hover:bg-green-50 transition-colors flex items-center text-xs font-medium border border-green-200 bg-white px-2"
                                        >
                                            <PlusIcon className="w-4 h-4 mr-1"/> Carga CSV
                                        </button>
                                        {is183150 && (
                                            <button 
                                                onClick={() => openAddBankModal(docData.id)}
                                                className="text-sky-600 hover:text-sky-800 p-1 rounded hover:bg-sky-50 transition-colors flex items-center text-xs font-medium border border-sky-200 bg-white px-2"
                                            >
                                                <PlusIcon className="w-4 h-4 mr-1"/> Agregar Banco
                                            </button>
                                        )}
                                        <button 
                                            onClick={() => handleDeleteDoc(docData.id)}
                                            className="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-50 transition-colors"
                                            title="Eliminar documento completo"
                                        >
                                            <TrashIcon className="w-5 h-5" />
                                        </button>
                                    </div>
                                </div>
                                
                                {!isCollapsed && (
                                    <div className="p-4 bg-white overflow-x-auto min-h-[100px] animate-fade-in-down">
                                        {Object.keys(docData.data).length === 0 ? (
                                            <p className="text-gray-400 italic text-sm text-center mt-4">Documento vacío</p>
                                        ) : (
                                            <div className="space-y-1">
                                                {Object.entries(docData.data)
                                                    .sort(([a], [b]) => a.localeCompare(b))
                                                    .map(([key, val]) => (
                                                    <FirestoreTreeItem 
                                                        key={key} 
                                                        name={key} 
                                                        value={val} 
                                                        path={[docData.id, key]}
                                                        depth={0} 
                                                        onEditValue={handleEditValue}
                                                        onAddChild={handleAddChild}
                                                        onDelete={handleDelete}
                                                    />
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* MODALS */}
            <CreateInmobiliariaModal 
                isOpen={isCreateModalOpen} 
                onClose={() => setIsCreateModalOpen(false)} 
                onSave={handleCreateSave} 
                parentDocId={createTargetDocId}
            />

            {editValueModal && (
                <EditValueModal 
                    isOpen={editValueModal.open} 
                    onClose={() => setEditValueModal(null)} 
                    initialValue={editValueModal.value}
                    path={editValueModal.path}
                    onSave={handleSaveValue}
                />
            )}

            {addFieldModal && (
                <AddFieldModal
                    isOpen={addFieldModal.open}
                    onClose={() => setAddFieldModal(null)}
                    parentPath={addFieldModal.parentPath}
                    onSave={handleSaveNewField}
                />
            )}

            {isCsvModalOpen && csvTargetDocId && (
                <AdminInmobiliariaCsvModal
                    isOpen={isCsvModalOpen}
                    onClose={() => { setIsCsvModalOpen(false); fetchDocs(); }}
                    initialDocId={csvTargetDocId}
                    existingDocs={docs.map(d => ({
                        id: d.id,
                        name: getDocDisplayName(d.id, d.data),
                        subKeys: Object.keys(d.data)
                    }))}
                />
            )}
        </>
    );
};

